import cookies from '../utils/cookies';
import { seThemeBaseUrl, setApiBaseUrl } from '../utils/env';
import http from './http';
import type { GetProductsParams, GetProductsByIdsParams, PayloadObj, BillingInfo } from '../types/services';
import { validate as isUUID} from 'uuid';

// https://ascservices.amersc.com/Products/api/v1/Public/GetProductsBy/?host=www.ustoperatortraining.com&couponcode=null&unpublished=false
// https://ascservices.amersc.com/Carts/api/v1/CartProducts/?completeProducts=false&unpublished=false
// https://ascservices.amersc.com/Carts/api/v1/CartProducts/b120b052-f418-410a-a4ec-1fde8adc3e32/1?completeProducts=false
// https://themes.amersc.com/shop/templates/ustoperatortraining.com/css/shop.css"

let THEME_BASE_URL = seThemeBaseUrl('https://dlthemes.amersc.com/shop/templates');
let API_BASE_URL = setApiBaseUrl('https://api.certus.com');
const UNPUBLISHED = cookies.get('unpublished') === '1';

const queryOrCookie = (explicit: string | null | undefined, cookieKey: string, queryName: string) => {
  if (explicit != null && explicit !== '') return `&${queryName}=${explicit}`;
  const cVal = cookies.get(cookieKey);
  return cVal ? `&${queryName}=${cVal}` : '';
};

const api = {
  getTemplate: (domain: string, signal?: AbortSignal) => {
    return http.get(`${THEME_BASE_URL}/${domain}/index.html`, { signal });
  },

  // https://api.certus.com/Products/api/v2/Public/GetProductsBy?host=certus.colm&groupId=08287bfd-1948-47f8-8be0-0484bd1d9da0&testPrice=A&couponcode=coupon&fullSiteBuild=false&unpublished=false
  getProducts: (data: GetProductsParams, signal?: AbortSignal) => {
    const { host, testPrice, groupId, coupon, fullSiteBuild, includeUpsells } = data;

    const testPriceQuery = queryOrCookie(testPrice, '_up4tp', 'testPrice');
    const groupIdQuery = queryOrCookie(groupId, '_up4gid', 'groupId');
    const couponQuery = queryOrCookie(coupon, '_up4cc', 'couponcode');

    const fullSiteBuildQuery = fullSiteBuild !== undefined ? `&fullSiteBuild=${fullSiteBuild}` : '&fullSiteBuild=true';
    const includeUpsellsQuery = includeUpsells !== undefined ? `&includeUpsells=${includeUpsells}` : '&includeUpsells=false';
    const unpublishedQuery = `&unpublished=${UNPUBLISHED}`;

    const url = `${API_BASE_URL}/Products/api/v2/Public/GetProductsBy/?host=${host}${testPriceQuery}${groupIdQuery}${couponQuery}${unpublishedQuery}${fullSiteBuildQuery}${includeUpsellsQuery}`;

    return http.get(url, { signal });
  },

  getProductsByIds: (data: GetProductsByIdsParams, signal?: AbortSignal) => {
    const { productIds, testPrice, groupId, coupon, fullSiteBuild, includeUpsells } = data;

    const productIdsQuery = productIds.map((id) => `productIds=${id}`).join('&');

    const testPriceQuery = queryOrCookie(testPrice, '_up4tp', 'testPrice');
    const groupIdQuery = queryOrCookie(groupId, '_up4gid', 'groupId');
    const couponQuery = queryOrCookie(coupon, '_up4cc', 'couponcode');

    const fullSiteBuildQuery = fullSiteBuild !== undefined ? `&fullSiteBuild=${fullSiteBuild}` : '&fullSiteBuild=true';
    const includeUpsellsQuery = includeUpsells !== undefined ? `&includeUpsells=${includeUpsells}` : '&includeUpsells=false';
    const unpublishedQuery = `&unpublished=${UNPUBLISHED}`;

    const url = `${API_BASE_URL}/Products/api/v2/Public/GetProductsByIds/?${productIdsQuery}${groupIdQuery}${testPriceQuery}${couponQuery}${unpublishedQuery}${fullSiteBuildQuery}${includeUpsellsQuery}`;

    return http.get(url, { signal });
  },

  getCart: (cartId: string, signal?: AbortSignal) => {
    const timestamp = new Date().getTime();
    const cacheBust = `&timestamp=${timestamp}`;

    const url = `${API_BASE_URL}/Carts/api/v1/Carts/${cartId.toLowerCase()}/?completeProducts=false&unpublished=${UNPUBLISHED}${cacheBust}`;
    return http.get(url, { signal });
  },

  // createCart: (cartData: object) => {
  //   const { productId, quantity, pricingUsed, groupId, coupon, sourceHost, selectedUpsellProductIds } =
  //     cartData as PayloadObj;
  //   const data = {
  //     cartId: null, // set to null because we are creating a new cart
  //     productId,
  //     quantity,
  //     pricingUsed: pricingUsed?.toLowerCase() || 'A',
  //     groupId: groupId?.toLowerCase() || null,
  //     couponCode: coupon?.toLowerCase() || null,
  //     sourceHost: sourceHost?.toLowerCase() || null,
  //     selectedUpsellProductIds: selectedUpsellProductIds || [],
  //   };
  //   return axios.post(`${API_BASE_URL}/Carts/api/v1/CartProducts/?completeProducts=false&unpublished=${UNPUBLISHED}`, data);
  // },

  addProduct: (cartData: object) => {
    const { cartId, productId, quantity, pricingUsed, groupId, coupon, sourceHost, selectedUpsellProductIds } =
      cartData as PayloadObj;
    const data = {
      cartId,
      productId,
      quantity,
      pricingUsed: pricingUsed?.toLowerCase() || 'A',
      groupId: groupId?.toLowerCase() || null,
      couponCode: coupon?.toLowerCase() || null,
      sourceHost: sourceHost?.toLowerCase() || null,
      selectedUpsellProductIds: selectedUpsellProductIds || [],
      aspNetUserId: isUUID(cookies.get('catalogUID')) ? cookies.get('catalogUID')?.toLowerCase() : null,
    };

    // setting completedProducts=true so that only selected upsells are returned by the cart API
    return http.post(`${API_BASE_URL}/Carts/api/v1/CartProducts/?completeProducts=false&unpublished=${UNPUBLISHED}`, data);
  },

  // this also updates quantity of selected upsells
  updateProduct: (productData: object) => {
    const { cartId, productId, cartProductId, quantity, isSelected } = productData as PayloadObj;
    const data = {
      cart_ProductId: cartProductId, // upsell cart_ProductId
      cartId: cartId, // cartId
      productId: productId, // main productId
      quantity: quantity, // use quantity of main product or you can leave 1, it will automatically use the main product's quantity
      isSelected: isSelected, // true/false, add or remove upsell
    };

    return http.put(`${API_BASE_URL}/Carts/api/v1/CartProducts/?completeProducts=false&unpublished=${UNPUBLISHED}`, data);
  },

  // this adds the selected upsell to the cart, needs to use the parent's quantity
  updateUpsell: (upsellData: object) => {
    const { cartId, productId, cartProductId, quantity, isSelected } = upsellData as PayloadObj;
    const data = {
      cart_ProductId: cartProductId, // upsell cart_ProductId
      cartId: cartId, // cartId
      productId: productId,
      quantity: quantity, // use quantity of main product or you can leave 1, it will automatically use the main product's quantity
      isSelected: isSelected, // true/false, add or remove upsell
    };

    return http.put(
      `${API_BASE_URL}/Carts/api/v1/CartProducts/UpdateUpsell/?completeProducts=false&unpublished=${UNPUBLISHED}`,
      data
    );
  },

  removeProduct: (cartId: string, cartProductId: number) => {
    return http.delete(
      `${API_BASE_URL}/Carts/api/v1/CartProducts/${cartId}/${cartProductId}/?completeProducts=false&unpublished=${UNPUBLISHED}`
    );
  },

  updateCartBillingInfo: (billingInfo: object) => {
    const { cartId, firstName, lastName, email } = billingInfo as BillingInfo;
    const data = {
      cartId,
      billingFirstName: firstName,
      billingLastName: lastName,
      billingEmail: email,
    };

    return http.put(`${API_BASE_URL}/Carts/api/v1/Carts/UpdateCartBillingInfo`, data);
  },
};

export default api;
